/*
  SHLC nav v2 behaviour. Markup: snippets/shlc-nav-bar.liquid + snippets/shlc-nav-drawer.liquid.

  Desktop (>= 1340px): panels open the moment the mouse enters an item and close the moment it leaves
  the header + panel (no timers, per the 2026-09-29 brief). Item names are real links; the caret button
  (or the whole button for Past Papers) toggles by click, tap or keyboard. Esc closes and returns focus.
  Panels are position:fixed under the header, re-measured on open, scroll and resize.

  Phone (< 1340px): the drawer is moved to <body>, opens as a modal with focus kept inside, one
  accordion section open at a time, and a filter box that searches every link in the drawer.
*/
(() => {
  const DESKTOP = '(min-width: 1340px)';
  const FINE = '(hover: hover) and (pointer: fine)';
  const mq = (q) => window.matchMedia(q).matches;
  const path = location.pathname.replace(/\/$/, '') || '/';
  const samePath = (a) => { try { return (new URL(a.href, location.origin).pathname.replace(/\/$/, '') || '/') === path; } catch (e) { return false; } };

  /* ---------------- desktop ---------------- */
  function initBar(bar) {
    if (!bar || bar.dataset.snavReady) return;
    bar.dataset.snavReady = '1';
    const dim = document.querySelector('[data-snav-dim]');
    const wrapper = bar.closest('.header-wrapper') || bar.closest('header') || bar;
    const items = [...bar.querySelectorAll('[data-snav-item]')];
    const hoverable = () => mq(DESKTOP) && mq(FINE);
    let openItem = null, openedBy = null;

    // panel sits flush under the header, with its content on the logo / icon edges
    const measure = () => {
      const root = document.documentElement.style;
      root.setProperty('--snav-top', Math.max(0, Math.round(wrapper.getBoundingClientRect().bottom)) + 'px');
      const logo = wrapper.querySelector('.header__heading-link, .header__heading');
      const icons = wrapper.querySelector('.header__icons');
      if (logo) root.setProperty('--snav-l', Math.round(logo.getBoundingClientRect().left) + 'px');
      if (icons) root.setProperty('--snav-rp', Math.max(16, Math.round(document.documentElement.clientWidth - icons.getBoundingClientRect().right)) + 'px');
    };
    // v4: the panel is only as wide as its content; sit it under its own item, clamped to the viewport
    const place = (item, panel) => {
      const vw = document.documentElement.clientWidth, w = panel.offsetWidth;
      const left = Math.round(item.getBoundingClientRect().left) - 14;
      // right limit = the header's content edge (right of the cart icon), never past it
      const icons = wrapper.querySelector('.header__icons');
      const rightEdge = icons ? Math.round(icons.getBoundingClientRect().right) : vw - 16;
      panel.style.left = Math.max(16, Math.min(left, rightEdge - w)) + 'px';
    };
    const toggles = (item) => item.querySelectorAll('[data-snav-tog]');
    const close = (focusBack) => {
      if (!openItem) return;
      const item = openItem;
      item.classList.remove('is-open');
      item.querySelector('[data-snav-panel]').hidden = true;
      toggles(item).forEach((b) => b.setAttribute('aria-expanded', 'false'));
      if (dim) dim.hidden = true;
      document.documentElement.classList.remove('snav-menu-open');
      openItem = null; openedBy = null;
      if (focusBack) { const t = item.querySelector('[data-snav-tog]'); if (t) t.focus(); }
    };
    const open = (item, by) => {
      if (openItem === item) { openedBy = by; return; }
      if (openItem) close(false);
      measure();
      item.classList.add('is-open');
      const panel = item.querySelector('[data-snav-panel]');
      panel.hidden = false;
      place(item, panel);
      toggles(item).forEach((b) => b.setAttribute('aria-expanded', 'true'));
      if (dim) dim.hidden = false;
      document.documentElement.classList.add('snav-menu-open');
      openItem = item; openedBy = by;
    };

    // Instant, no timers. Entering an item opens it (or switches straight to it). The panel is a DOM
    // descendant of the header wrapper, so the header's lower padding is the corridor from item to panel
    // and needs no hover bridge. Leaving the wrapper, touching another header control, or moving onto the
    // dimmed page closes at once.
    // Aim guard: with a panel already open, a pointer travelling DOWN (towards the panel) that crosses a
    // neighbouring item does not switch unless it stops there for AIM_REST ms. Sideways moves along the
    // bar still switch instantly, and the first open is always instant.
    const AIM_REST = 120;
    let lastY = null, aimTimer = 0;
    wrapper.addEventListener('pointermove', (e) => { lastY = e.clientY; }, { passive: true });
    items.forEach((item) => {
      item.addEventListener('pointerenter', (e) => {
        if (e.pointerType !== 'mouse' || !hoverable()) return;
        clearTimeout(aimTimer);
        const dy = lastY === null ? 0 : e.clientY - lastY;
        lastY = e.clientY;
        if (openItem && openItem !== item && openedBy === 'pointer' && dy > 1) {
          aimTimer = setTimeout(() => open(item, 'pointer'), AIM_REST);
          return;
        }
        open(item, 'pointer');
      });
      item.addEventListener('pointerleave', () => clearTimeout(aimTimer));
    });
    wrapper.addEventListener('pointerleave', (e) => {
      if (e.pointerType === 'mouse' && openedBy === 'pointer') close(false);
    });
    wrapper.addEventListener('pointerover', (e) => {
      if (e.pointerType !== 'mouse' || openedBy !== 'pointer') return;
      const t = e.target;
      if (t.closest('[data-snav-item]')) return;
      if (t.closest('a, button, input, .header__icons, .snav-bar-end')) close(false);
    });
    if (dim) {
      dim.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse' && openedBy === 'pointer') close(false); });
      dim.addEventListener('click', () => close(false));
    }
    // click / tap / keyboard on the caret (or the Past Papers button) toggles
    bar.addEventListener('click', (e) => {
      const b = e.target.closest('[data-snav-tog]');
      if (!b) return;
      e.preventDefault();
      const item = b.closest('[data-snav-item]');
      const by = e.detail === 0 ? 'key' : 'click';
      if (item === openItem && openedBy !== 'pointer') close(false); else open(item, by);
    });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && openItem) close(true); });
    document.addEventListener('click', (e) => { if (openItem && !bar.contains(e.target)) close(false); });
    bar.addEventListener('focusout', (e) => { if (openItem && openedBy !== 'pointer' && e.relatedTarget && !bar.contains(e.relatedTarget)) close(false); });
    window.addEventListener('scroll', () => { if (openItem) measure(); }, { passive: true });
    window.addEventListener('resize', () => { if (!openItem) return; if (!mq(DESKTOP)) close(false); else { measure(); place(openItem, openItem.querySelector('[data-snav-panel]')); } });

    // current page: underline the matching link and mark its section
    items.forEach((item) => {
      let hit = false;
      item.querySelectorAll('a[href]').forEach((a) => { if (samePath(a)) { a.setAttribute('aria-current', 'page'); hit = true; } });
      if (hit && path !== '/') item.classList.add('is-current');
    });
  }

  /* ---------------- drawer ---------------- */
  function initDrawer(drawer) {
    if (!drawer || drawer.dataset.snavReady) return;
    drawer.dataset.snavReady = '1';
    // The theme editor re-renders the header: drop the copy an earlier render moved to <body>.
    document.querySelectorAll('body > .snav-drawer[data-snav-ready], body > .snav-scrim').forEach((el) => { if (el !== drawer) el.remove(); });
    document.documentElement.classList.remove('snav-lock');
    document.body.appendChild(drawer);
    const scrim = document.createElement('div');
    scrim.className = 'snav-scrim';
    scrim.setAttribute('data-snav', '');
    document.body.appendChild(scrim);

    const body = drawer.querySelector('[data-snav-body]');
    const q = drawer.querySelector('[data-snav-q]');
    const results = drawer.querySelector('[data-snav-results]');
    const secsEl = drawer.querySelector('[data-snav-secs]');
    const lf = drawer.querySelector('[data-snav-lf]');
    const closeBtn = drawer.querySelector('[data-snav-close]');
    let opener = null, openSec = null;

    const setOpen = (on) => {
      drawer.classList.toggle('is-open', on);
      scrim.classList.toggle('is-open', on);
      drawer.inert = !on;
      document.documentElement.classList.toggle('snav-lock', on);
      document.querySelectorAll('[data-snav-open]').forEach((b) => b.setAttribute('aria-expanded', on ? 'true' : 'false'));
      if (on) setTimeout(() => closeBtn && closeBtn.focus(), 60);
      else if (opener) opener.focus();
    };
    document.addEventListener('click', (e) => {
      const b = e.target.closest('[data-snav-open]');
      if (b) { opener = b; setOpen(true); }
    });
    closeBtn.addEventListener('click', () => setOpen(false));
    scrim.addEventListener('click', () => setOpen(false));
    drawer.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { setOpen(false); return; }
      if (e.key !== 'Tab') return;
      const f = [...drawer.querySelectorAll('a[href],button,input')].filter((el) => el.offsetParent !== null && !el.closest('[inert]'));
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
    window.addEventListener('resize', () => { if (mq(DESKTOP) && drawer.classList.contains('is-open')) setOpen(false); });

    const sync = () => {
      drawer.querySelectorAll('[data-snav-sec]').forEach((sec) => {
        const on = sec.dataset.snavSec === openSec;
        sec.classList.toggle('is-open', on);
        sec.querySelector('[data-snav-m]').setAttribute('aria-expanded', on ? 'true' : 'false');
        sec.querySelector('.snav-acc').inert = !on;
      });
    };
    drawer.addEventListener('click', (e) => {
      const b = e.target.closest('[data-snav-m]');
      if (!b) return;
      openSec = openSec === b.dataset.snavM ? null : b.dataset.snavM;
      sync();
      // offsetTop is measured from the drawer (it includes the 58px header), so use rects: the opened section's title row lands at the top
      if (openSec) setTimeout(() => { const sec = b.closest('.snav-sec'); body.scrollTop += sec.getBoundingClientRect().top - body.getBoundingClientRect().top; }, 60);
    });

    // current page
    drawer.querySelectorAll('.snav-sec').forEach((sec) => {
      let hit = false;
      sec.querySelectorAll('a[href]').forEach((a) => { if (samePath(a)) { a.setAttribute('aria-current', 'page'); hit = true; } });
      if (hit && path !== '/') sec.classList.add('is-current');
    });

    // filter: every link in the drawer sections, labelled with its section and group
    const index = [];
    const seen = {};
    drawer.querySelectorAll('.snav-sec').forEach((sec) => {
      const secLabel = sec.dataset.snavLabel || '';
      sec.querySelectorAll('a[href]').forEach((a) => {
        const grp = a.closest('[data-snav-group]');
        const t = (a.querySelector('.snav-t') || a).textContent.trim().replace(/\s+/g, ' ');
        const label = a.getAttribute('aria-label') || t;
        const key = a.getAttribute('href') + '|' + label;
        if (!label || seen[key]) return;
        seen[key] = 1;
        index.push({ t: label, href: a.getAttribute('href'), where: secLabel + (grp ? ' › ' + grp.dataset.snavGroup : '') });
      });
    });
    const escH = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
    q.addEventListener('input', () => {
      const v = q.value.trim().toLowerCase();
      const on = !!v;
      results.hidden = !on; secsEl.hidden = on; lf.hidden = on;
      if (!on) { results.innerHTML = ''; return; }
      const words = v.split(/\s+/);
      const hits = index.filter((r) => { const h = (r.t + ' ' + r.where).toLowerCase(); return words.every((w) => h.indexOf(w) >= 0); })
        .sort((a, b) => (a.t.toLowerCase().indexOf(words[0]) < 0) - (b.t.toLowerCase().indexOf(words[0]) < 0)).slice(0, 12);
      results.innerHTML = '<div class="snav-mres-l">' + (hits.length ? hits.length + (hits.length === 1 ? ' match' : ' matches') : 'No matches. Try fractions, papers or Year 5') + '</div>' +
        hits.map((r) => '<a href="' + escH(r.href) + '"><span class="snav-t">' + escH(r.t) + '</span><span class="snav-s">' + escH(r.where) + '</span></a>').join('');
    });
  }

  const start = () => {
    initBar(document.querySelector('[data-snav-bar]'));
    initDrawer(document.querySelector('[data-snav-drawer]:not([data-snav-ready])'));
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  document.addEventListener('shopify:section:load', start);
})();
